import {
  faBuildingColumns,
  faCheckCircle,
  faCreditCard,
  faKeyboard,
  faStar,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { faEllipsisVertical, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Dialog } from 'src/components/Dialog';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { List, ListItem } from 'src/components/ListItem';
import { PaymentMethodForm } from 'src/components/PaymentMethodForm';
import { getBacsRedirectUrl } from 'src/components/PaymentMethodForm';
import { SectionHeader } from 'src/components/SectionHeader';
import { TerminalPaymentMethodForm } from 'src/components/TerminalPaymentMethodForm';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { PaymentMethod } from 'src/models/payment-methods/_types';
import {
  createPaymentMethod,
  deletePaymentMethod,
  fetchPaymentMethod,
  fetchPaymentMethods,
  makePaymentMethodDefault,
} from 'src/models/payment-methods/payment-methods';
import { getPaymentMethodIcon } from 'src/models/payment-methods/utils';
import { getTerminals } from 'src/models/terminal/terminal';
import { User } from 'src/models/user/_types';
import { isHeadTrainer } from 'src/models/user/utils';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { formatMonthDayYear } from 'src/utils/dateFormatter';
import { capitalizeFirstLetter } from 'src/utils/stringFormatter';
import toast from 'src/utils/toast';

interface PaymentMethodsTabProps {
  user?: User;
}

const PaymentMethodsTab: React.FC<PaymentMethodsTabProps> = ({ user }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'client.payments.paymentMethods',
  });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });

  const { session } = useSession();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSwipeDialogOpen, setIsSwipeDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const [confirmProps, confirm] = useConfirmDialogComponent({
    title: t('cancelRemoveTitle'),
  });
  const queryKey = ['paymentMethods', user?.id];

  const { data: paymentMethods = [], isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: () => user && fetchPaymentMethods(user.id),
    enabled: !!user,
  });

  const { data: onlineReaders = [] } = useQuery({
    queryKey: ['terminals'],
    queryFn: () => getTerminals({}),
    select: (data) => data.filter((terminal) => terminal.status === 'online'),
  });

  const { mutate: makeDefault } = useMutation({
    mutationFn: makePaymentMethodDefault,
    onSuccess: () => {
      toast.success(t('setDefaultSuccess'));
      queryClient.invalidateQueries({
        queryKey: queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: ['paymentMethods', 'accessible', user?.id],
      });
    },
    onError: (error: Error) => {
      toast.error(api.formatError(error, t('toasts.unableToSetDefault')));
    },
  });

  const { mutate: deleteMethod } = useMutation({
    mutationFn: deletePaymentMethod,
    onSuccess: () => {
      toast.success(t('methodRemovedSuccess'));
      queryClient.invalidateQueries({
        queryKey: queryKey,
      });
    },
    onError: (error: Error) => {
      toast.error(api.formatError(error, t('toasts.unableToDelete')));
    },
  });

  const { mutate: createMethod } = useMutation({
    mutationFn: createPaymentMethod,
    onSuccess: () => {
      toast.success(t('methodAddedSuccess'));
      queryClient.invalidateQueries({
        queryKey: queryKey,
      });
    },
    onError: (error: Error) => {
      toast.error(api.formatError(error, t('toasts.unableToAdd')));
    },
  });

  const handleDelete = async (pm: PaymentMethod) => {
    const confirmed = await confirm({
      title: t('cancelRemoveTitle'),
      message: t('cancelRemoveMessage'),
      confirmText: t('cancelRemove'),
    });

    if (confirmed) {
      deleteMethod(pm.id);
    }
  };
  const currencyCode = session?.defaultCurrency;
  const isUK = currencyCode === 'gbp';

  const handleBacsRedirect = async () => {
    if (!user) return;

    // Open the popup synchronously here so iOS/iPadOS Safari keeps it tied to
    // the user gesture. The setup intent fetch happens after, and we navigate
    // the already-opened window once we have the URL.
    const popup = window.open('about:blank', '_blank');

    try {
      await getBacsRedirectUrl(
        user?.id,
        async () => {
          queryClient.invalidateQueries({
            queryKey: queryKey,
          });
          toast.success(t('setupBACSComplete'));
        },
        popup
      );
    } catch (error) {
      if (popup && !popup.closed) {
        popup.close();
      }
      toast.error(t('setupBACSError'));
    }
  };

  const [verifyingPaymentMethodId, setVerifyingPaymentMethodId] =
    useState<Identifier | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingCountRef = useRef(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const startVerificationPolling = (paymentMethodId: PaymentMethod['id']) => {
    if (!user) return;
    setIsPolling(true);
    setVerifyingPaymentMethodId(paymentMethodId);
    pollingCountRef.current = 0;

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      pollingCountRef.current += 1;

      try {
        const updatedPm = await fetchPaymentMethod(
          user.id,
          paymentMethodId,
          true
        );
        const currentPm = paymentMethods.find(
          (pm) => pm.id === paymentMethodId
        );

        if (
          currentPm?.status === 'unverified' &&
          updatedPm.status === 'verified'
        ) {
          queryClient.setQueryData(
            queryKey,
            (old: PaymentMethod[] | undefined) => {
              if (!old) return [updatedPm];
              return old.map((pm) =>
                pm.id === paymentMethodId ? updatedPm : pm
              );
            }
          );
          toast.success(t('toasts.bankVerified'));
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          setIsPolling(false);
          setVerifyingPaymentMethodId(null);
          return;
        }
      } catch (error) {
        // error
      }

      if (pollingCountRef.current >= 2) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        setIsPolling(false);
        setVerifyingPaymentMethodId(null);
      }
    }, 3000);
  };

  const handleVerifyRedirect = (pm: PaymentMethod) => {
    if (pm.status === 'unverified' && pm.microdepositUrl) {
      const newWindow = window.open(pm.microdepositUrl, '_blank');

      if (newWindow) {
        const checkClosed = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(checkClosed);
            startVerificationPolling(pm.id);
          }
        }, 500);
      }
    }
  };

  const renderPaymentMethod = (pm: PaymentMethod) => (
    <Card key={pm.id} sx={{ mb: 2, p: 2 }}>
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box display="flex" alignItems="center">
          <Icon
            size="medium"
            icon={getPaymentMethodIcon(pm.paymentMethodType)}
            sx={{ mr: 3, ml: 1 }}
          />
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('brandEndingIn', {
                brand: capitalizeFirstLetter(pm.brand),
                last4: pm.last4,
              })}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t('added', { date: formatMonthDayYear(pm.createdAt) })}
            </Typography>
            {pm.expMonth && pm.expYear && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('expiresOn', { month: pm.expMonth, year: pm.expYear })}
              </Typography>
            )}
            {pm.status === 'unverified' && (
              <Typography variant="body2" color="error.main" gutterBottom>
                {isPolling && verifyingPaymentMethodId === pm.id
                  ? t('bankVerificationChecking')
                  : t('bankVerificationRequired')}
              </Typography>
            )}
            {pm.default && (
              <Typography variant="body2" color="primary">
                {t('defaultPaymentMethod')}
              </Typography>
            )}
          </Box>
        </Box>
        <DropdownMenu
          trigger={
            <IconButton size="small">
              <Icon icon={faEllipsisVertical} />
            </IconButton>
          }
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <List sx={{ py: 0 }}>
            {pm.status === 'unverified' && pm.microdepositUrl && (
              <ListItem
                onClick={() => handleVerifyRedirect(pm)}
                icon={faCheckCircle}
                primary={t('billingVerificationTitle')}
                clickable
              />
            )}
            {!pm.default && (
              <ListItem
                onClick={() => makeDefault(pm.id)}
                icon={faStar}
                primary={t('makeDefault')}
                clickable
              />
            )}
            <ListItem
              onClick={() => handleDelete(pm)}
              icon={faTrash}
              primary={t('cancelRemove')}
              clickable
            />
          </List>
        </DropdownMenu>
      </Box>
    </Card>
  );

  const dialogActions = [
    { label: tCommon('buttons.cancel'), onClick: () => setIsDialogOpen(false) },
  ];

  return (
    <Box>
      <SectionHeader
        title={t('title')}
        sx={{ px: 0, mb: 1 }}
        actions={
          <>
            {onlineReaders.length > 0 ? (
              <DropdownMenu
                trigger={
                  <Button
                    variant="bordered"
                    color="primary"
                    startIcon={<Icon icon={faPlus} />}
                  >
                    {t('addMethod')}
                  </Button>
                }
              >
                <List sx={{ py: 0 }}>
                  <ListItem
                    onClick={() => setIsDialogOpen(true)}
                    icon={faKeyboard}
                    primary={t('manualEntry')}
                    clickable
                  />
                  <ListItem
                    onClick={() => setIsSwipeDialogOpen(true)}
                    icon={faCreditCard}
                    primary={t('swipeCard')}
                    clickable
                  />
                  {isUK && !isHeadTrainer(user) && (
                    <ListItem
                      onClick={handleBacsRedirect}
                      icon={faBuildingColumns}
                      primary={t('addBankAccount')}
                      clickable
                    />
                  )}
                </List>
              </DropdownMenu>
            ) : isUK && !isHeadTrainer(user) ? (
              <DropdownMenu
                trigger={
                  <Button
                    variant="bordered"
                    color="primary"
                    startIcon={<Icon icon={faPlus} />}
                  >
                    {t('addMethod')}
                  </Button>
                }
              >
                <List sx={{ py: 0 }}>
                  <ListItem
                    onClick={() => setIsDialogOpen(true)}
                    icon={faCreditCard}
                    primary={t('addCreditCard')}
                    clickable
                  />
                  <ListItem
                    onClick={handleBacsRedirect}
                    icon={faBuildingColumns}
                    primary={t('addBankAccount')}
                    clickable
                  />
                </List>
              </DropdownMenu>
            ) : (
              <Button
                variant="bordered"
                color="primary"
                onClick={() => setIsDialogOpen(true)}
                startIcon={<Icon icon={faPlus} />}
              >
                {t('addMethod')}
              </Button>
            )}
          </>
        }
      />
      {isLoading ? (
        <Typography>{t('loading')}</Typography>
      ) : paymentMethods.length === 0 ? (
        <Typography>{t('noMethods')}</Typography>
      ) : (
        paymentMethods.map(renderPaymentMethod)
      )}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={t('addMethod')}
        actions={dialogActions}
        fullWidth
      >
        {user && (
          <PaymentMethodForm
            user={user}
            handlePaymentMethodAdded={(response) => {
              createMethod({
                token: response.setupIntent.id,
                userId: user.id,
                paymentMethodType: response.setupIntent.payment_method?.type,
                status:
                  response.setupIntent.status === 'requires_action'
                    ? 'unverified'
                    : 'verified',
                default: false,
              });
              setIsDialogOpen(false);
            }}
          />
        )}
      </Dialog>
      {user && (
        <TerminalPaymentMethodForm
          open={isSwipeDialogOpen}
          onClose={() => setIsSwipeDialogOpen(false)}
          user={user}
          handlePaymentMethodAdded={(response) => {
            createMethod({
              token: response.id,
              userId: user.id,
              paymentMethodType: 'card',
              status: 'verified',
              default: false,
            });
          }}
        />
      )}
      <ConfirmDialog {...confirmProps} />
    </Box>
  );
};

export default PaymentMethodsTab;
